/**
 * Add-Event (F6) master data, required-field rules, validation and payload builder — parity of
 * `fmdashboard.controller.js` `GetOOFFlyOutMasterData` (:1540), `isShowAsRequired`/`isTechnologyRequired`/
 * `isCommentRequired` (:1327-1356), `validateFormDetail`, and `addNPAssignment` (:1462-1516). Pure and
 * side-effect-free so the flyout logic is unit-tested and reused without the FXP shell.
 */
import { AddEventForm, EventTypeOption, TechnologyOption } from '../../../contracts/FMPendingContracts';
import { NonProjectAssignmentRequest } from '../../../contracts/FMGrmContracts';

/** Recurrence options (parity: GetOOFFlyOutMasterData RecurrenceData). */
export const RECURRENCE_OPTIONS = ['None', 'Daily', 'Weekly', 'Monthly'];
/** Show-As options (parity: GetOOFFlyOutMasterData ShowAs). */
export const SHOWAS_OPTIONS = ['Select', 'Busy', 'Free'];
/**
 * A small default time-zone list to seed the dropdown locally (the deployed app fetches the full
 * list from OneProfile.GetTimeZones). The resource's own time zone is added + preselected by the panel.
 */
export const COMMON_TIMEZONES = [
  'UTC',
  '(UTC-08:00) Pacific Time (US & Canada)',
  '(UTC-06:00) Central Time (US & Canada)',
  '(UTC-05:00) Eastern Time (US & Canada)',
  '(UTC+00:00) Dublin, Edinburgh, Lisbon, London',
  '(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
  '(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi',
  '(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi',
];

/** Find a selected event type by id (parity: getObjFromArray on EventTypeId). */
export function findEventType(
  eventTypes: EventTypeOption[],
  eventTypeId?: number
): EventTypeOption | undefined {
  if (eventTypeId === undefined || eventTypeId === null) {
    return undefined;
  }
  return eventTypes.find((e) => e.EventTypeId === eventTypeId);
}

/** Show As is required when the selected type is NOT busy (parity: `isShowAsRequired`). */
export function isShowAsRequired(eventType?: EventTypeOption): boolean {
  return !!eventType && eventType.IsBusy === false;
}

/** Technology is required when the selected type IsSpecialtyRequired (parity: `isTechnologyRequired`). */
export function isTechnologyRequired(eventType?: EventTypeOption): boolean {
  return !!eventType && eventType.IsSpecialtyRequired === true;
}

/** Comments are required when the selected type IsCommentRequired (parity: `isCommentRequired`). */
export function isCommentRequired(eventType?: EventTypeOption): boolean {
  return !!eventType && eventType.IsCommentRequired === true;
}

export type AddEventErrors = Partial<Record<
  'StartDateTime' | 'EndDateTime' | 'TimeZone' | 'EventTypeId' | 'ShowAs' | 'TechnologyId' | 'Comments' | 'Recurrence',
  string
>>;

/**
 * Validate the add-event form (parity: `validateFormDetail` + the required-field rules). Returns a
 * map of field → message; an empty map means valid.
 */
export function validateAddEvent(
  form: AddEventForm,
  eventTypes: EventTypeOption[]
): AddEventErrors {
  const errors: AddEventErrors = {};
  const evt = findEventType(eventTypes, form.EventTypeId);

  if (!form.StartDateTime) {
    errors.StartDateTime = 'Please enter the From Date';
  }
  if (!form.EndDateTime) {
    errors.EndDateTime = 'Please enter the To Date';
  }
  if (form.StartDateTime && form.EndDateTime) {
    const start = new Date(form.StartDateTime);
    const end = new Date(form.EndDateTime);
    if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end < start) {
      errors.EndDateTime = 'To date/time cannot be less than From date/time';
    }
  }
  if (!form.TimeZone) {
    errors.TimeZone = 'Please select a value for TimeZone';
  }
  if (form.EventTypeId === undefined || form.EventTypeId === null) {
    errors.EventTypeId = 'Please select a value for Event Type';
  }
  if (isShowAsRequired(evt) && (!form.ShowAs || form.ShowAs.toLowerCase() === 'select')) {
    errors.ShowAs = 'Please select a value for Show As';
  }
  if (isTechnologyRequired(evt) && (form.TechnologyId === undefined || form.TechnologyId === null)) {
    errors.TechnologyId = 'Please select a value for Technology';
  }
  if (isCommentRequired(evt) && !(form.Comments && form.Comments.trim())) {
    errors.Comments = 'Please enter Comments';
  }
  const isRecurring = !!form.RecurrencePattern && form.RecurrencePattern.toLowerCase() !== 'none';
  if (isRecurring) {
    if (form.RecurrencePatternSelection === 'endsafter') {
      if (!form.RecurrenceEndsAfter || form.RecurrenceEndsAfter <= 0) {
        errors.Recurrence = 'Recurrence occurrence should be greater than 0';
      }
    } else if (form.RecurrencePatternSelection === 'endsby') {
      if (!form.RecurrenceEndsBy) {
        errors.Recurrence = 'Please select a recurrence end date';
      }
    }
  }
  return errors;
}

/**
 * Build the CreateNonProjectRequestAndSchedules payload from the form (parity: `addNPAssignment`).
 * Comments are truncated to 255 chars; ShowAs/IsBusy default to Busy; Technology is null unless the
 * type requires it; recurrence fields follow the ends-after/ends-by rules.
 */
export function buildNonProjectAssignment(
  form: AddEventForm,
  eventTypes: EventTypeOption[],
  technologies: TechnologyOption[],
  resourceAlias: string
): NonProjectAssignmentRequest {
  const evt = findEventType(eventTypes, form.EventTypeId);
  const comments = form.Comments ? form.Comments.slice(0, 255) : '';

  let isBusy = true;
  let showAs = 'Busy';
  if (form.ShowAs && form.ShowAs.toLowerCase() !== 'select') {
    isBusy = form.ShowAs.toLowerCase() === 'busy';
    showAs = form.ShowAs;
  }

  let technologyId: number | null = null;
  let technologyName: string | null = null;
  if (isTechnologyRequired(evt) && form.TechnologyId !== undefined) {
    const tech = technologies.find((t) => t.Id === form.TechnologyId);
    if (tech) {
      technologyId = tech.Id;
      technologyName = tech.Name;
    }
  }

  const isRecurrent = !!form.RecurrencePattern && form.RecurrencePattern.toLowerCase() !== 'none';
  let recurrenceEndsAfter = form.RecurrenceEndsAfter ?? 0;
  let recurrenceEndsBy = form.RecurrenceEndsBy ?? '';
  if (isRecurrent) {
    if (form.RecurrencePatternSelection === 'endsby') {
      recurrenceEndsAfter = 0;
    }
  } else {
    recurrenceEndsAfter = 0;
    recurrenceEndsBy = new Date().toISOString();
  }

  return {
    Id: 0,
    Comments: comments,
    StartDateTime: form.StartDateTime,
    EndDateTime: form.EndDateTime,
    EventType: evt ? evt.EventTypeName : '',
    ResourceAlias: resourceAlias,
    TimeZone: form.TimeZone,
    IsBusy: isBusy,
    ShowAs: showAs,
    TechnologyId: technologyId,
    TechnologyName: technologyName,
    IsRecurrent: isRecurrent,
    RecurrencePattern: form.RecurrencePattern ?? 'None',
    RecurrenceEndsAfter: recurrenceEndsAfter,
    RecurrenceEndsBy: recurrenceEndsBy,
  };
}
